import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chartText } from '@/i18n/chartText';
import { createPortal } from 'react-dom';

/**
 * 发品/实拍图等上传入口：点击后展示「本地文件 / 图片空间」下拉，而非直接打开文件选择器。
 */
export default function TemuUploadSourceMenu({
  open = false,
  anchorRef,
  onClose,
  onPickLocal,
  onPickImageSpace,
  disabled = false,
  localLabel = '本地文件',
  imageSpaceLabel = '图片空间',
}) {
  useTranslation('workbench');
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 132 });

  const updatePosition = useCallback(() => {
    const node = anchorRef?.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 132),
    });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const inAnchor = anchorRef?.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inAnchor && !inMenu) {
        onClose?.();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="temu-upload-source-menu"
      style={{ top: position.top, left: position.left, minWidth: position.width }}
      role="menu"
    >
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        onClick={() => {
          onClose?.();
          onPickLocal?.();
        }}
      >
        {localLabel === '本地文件' ? chartText('publishSpec.localFile', '本地文件') : localLabel}
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        onClick={() => {
          onClose?.();
          onPickImageSpace?.();
        }}
      >
        {imageSpaceLabel === '图片空间' ? chartText('publishImg.imageSpace', '图片空间') : imageSpaceLabel}
      </button>
    </div>,
    document.body,
  );
}
